import { Box } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { useAtdThemeContext } from "~/v3-poc/features/user-preferences";
import { useGroupSymbolMutation } from "../../shared/groups/useGroupSymbolMutation";
import type { OrderLeg } from "../../shared/optionLegs/optionLeg.types";
import {
  useWorkspace,
  useWorkspaceStore,
} from "../../shared/workspace/Workspace.context";
import { getEffectiveWidgetSymbol } from "../../shared/workspace/workspace.utils";
import { OrderTicketBody } from "./OrderTicketBody";
import "./OrderTicketWidget.css";

export interface OrderTicketWidgetConnectorProps {
  id: string;
}

const NO_LEGS: OrderLeg[] = [];

/**
 * Workspace order-ticket body: the v2 OrderTicket (and MultiLeg ticket)
 * wrapped with group symbol + routed option legs.
 */
export const OrderTicketWidget = ({ id }: OrderTicketWidgetConnectorProps) => {
  const { theme } = useAtdThemeContext();
  const store = useWorkspaceStore();
  const { setGroupSymbol } = useGroupSymbolMutation();
  const symbol = useWorkspace((s) => getEffectiveWidgetSymbol(s, id));
  const storedLegs = useWorkspace((s) => s.tickets[id]?.legs);
  const draft = useWorkspace((s) => s.tickets[id]?.draft);
  const legs = storedLegs ?? NO_LEGS;

  const onInteract = () => store.getState().touchTicket(id);

  const onRemoveLeg = (contractSymbol: string) => {
    const index = (store.getState().tickets[id]?.legs ?? []).findIndex(
      (leg) => leg.contract.symbol === contractSymbol,
    );
    if (index >= 0) {
      store.getState().removeTicketLeg(id, index);
    }
  };

  const onUpdateLegType = (contractSymbol: string, type: string) => {
    store.getState().updateTicketLegQuoteSide(id, contractSymbol, type);
  };

  const onCancel = () => store.getState().clearTicketLegs(id);

  const onSymbolSelect = (selected: string) => {
    const groupId = store.getState().widgets[id]?.groupId;
    if (groupId != null) {
      setGroupSymbol(groupId, selected);
      return;
    }
    store.getState().setTicketSymbol(id, selected);
  };

  return (
    <Box
      data-testid="order-ticket-widget"
      className={cn("workspace-order-ticket", theme === "dark" && "dark")}
      height="100%"
      width="100%"
      minHeight="0"
      minWidth="0"
      overflow="auto"
      onFocusCapture={onInteract}
    >
      <OrderTicketBody
        symbol={symbol}
        legs={legs}
        initialQuantity={draft?.quantity}
        onRemoveLeg={onRemoveLeg}
        onUpdateLegType={onUpdateLegType}
        onCancel={onCancel}
        onSymbolSelect={onSymbolSelect}
      />
    </Box>
  );
};
